import { supabase } from "@/supabase/client";

/**
 * Fetches data from a specified table in Supabase with various query parameters.
 *
 * @param {string} table - The name of the table to fetch data from.
 * @param {object} [query] - The query parameters for fetching data.
 * @param {string} [query.select] - The columns to select.
 * @param {object} [query.filter] - The filter conditions (supports date filtering).
 * @param {object} [query.order] - The order conditions.
 * @param {number} [query.limit] - The limit on the number of rows to fetch.
 * @param {number} [query.offset] - The number of rows to skip before starting to fetch.
 * @param {object} [dateFilters] - An object defining filtering conditions for date/timestamptz columns.
 * @returns {Promise<any>} - The fetched data or an error object.
 */
export async function fetchData(table, query = {}, dateFilters = {}) {
  try {
    // Add timeout configuration for large tables
    let request = supabase
      .from(table)
      .select(query.select || "*")
      .abortSignal(AbortSignal.timeout(30000)); // 30 second timeout

    // Apply filters
    if (query.filter) {
      for (const [column, condition] of Object.entries(query.filter)) {
        request = request.eq(column, condition);
      }
    }

    // Apply date/time filtering
    if (dateFilters) {
      for (const [column, filter] of Object.entries(dateFilters)) {
        if (filter.gt) {
          // Greater than a specific date (can be Unix timestamp or ISO string)
          request = request.gt(column, convertToISOOrTimestamp(filter.gt));
        }
        if (filter.lt) {
          // Less than a specific date (can be Unix timestamp or ISO string)
          request = request.lt(column, convertToISOOrTimestamp(filter.lt));
        }
        if (filter.eq) {
          // Equal to a specific date (can be Unix timestamp or ISO string)
          request = request.eq(column, convertToISOOrTimestamp(filter.eq));
        }
      }
    }

    // Apply ordering
    if (query.order) {
      for (const [column, direction] of Object.entries(query.order)) {
        request = request.order(column, { ascending: direction === "asc" });
      }
    }

    // Apply limit and offset
    if (query.limit) {
      request = request.limit(query.limit);
    }

    if (query.offset) {
      request = request.range(
        query.offset,
        query.offset + (query.limit || 100) - 1
      );
    }

    const { data, error } = await request;

    let newData = [];

    if (data) {
      for (let i = 0; i < data.length; i++) {
        let tempData = { ...data[i] };
        if (data[i].created_at) {
          tempData.timestamp_dt = parseInt(
            (new Date(data[i].created_at).getTime() / 1000).toFixed(0)
          );
          tempData.firebase_timestamp = {
            seconds: parseInt(
              (new Date(data[i].created_at).getTime() / 1000).toFixed(0)
            ),
            nanoseconds: 0,
            _seconds: parseInt(
              (new Date(data[i].created_at).getTime() / 1000).toFixed(0)
            ),
            _nanoseconds: 0,
          };

          // Find start of the day of created_at
          const startOfDay = new Date(data[i].created_at);
          startOfDay.setHours(0, 0, 0, 0);
          tempData.date_timestamp = parseInt(
            (startOfDay.getTime() / 1000).toFixed(0)
          );
        }

        newData.push(tempData);
      }
    }

    if (error) {
      throw new Error(`Error fetching data: ${error.message}`);
    }

    return { data: newData, error: null };
  } catch (error) {
    console.error("Error in fetchData:", error);
    return { error: error.message };
  }
}

/**
 * Converts a given date or Unix timestamp to ISO string format for Supabase queries.
 *
 * @param {string | number | Date} value - The date or timestamp to convert.
 * @returns {string} - The ISO date string or the original ISO string if already in that format.
 */
function convertToISOOrTimestamp(value) {
  if (typeof value === "number") {
    // If it's a Unix timestamp (seconds), convert to ISO string
    return new Date(value * 1000).toISOString();
  } else if (typeof value === "string" || value instanceof Date) {
    // If it's an ISO string or Date object, return its ISO representation
    return new Date(value).toISOString();
  }
  return value; // Return as is if it doesn't need conversion
}

/**
 * Fetches a single row from a specified table in Supabase based on a specific column value.
 *
 * @param {string} table - The name of the table to fetch data from.
 * @param {string} column - The column to filter by.
 * @param {string|number} value - The value to filter by.
 * @returns {Promise<object>} - The fetched row or an error object.
 */
export async function fetchSingleRow(table, column, value) {
  try {
    const { data, error } = await supabase
      .from(table)
      .select("*")
      .eq(column, value)
      .limit(1)
      .single();

    if (error) {
      throw new Error(`Error fetching single row: ${error.message}`);
    }

    return data;
  } catch (error) {
    throw new Error(`Error fetching single row: ${error.message}`);
  }
}

/**
 * Inserts or updates data in a specified table in Supabase.
 *
 * @param {string} table - The name of the table to insert or update data in.
 * @param {object} data - The data to be inserted or updated.
 * @param {string} [conflictColumn] - The column to check for conflicts (for upsert operations).
 * @returns {Promise<object>} - The inserted or updated data or an error object.
 */
export async function upsertData(table, data, conflictColumn) {
  try {
    let request;

    // Prepare the upsert request with or without conflict column
    if (conflictColumn) {
      request = supabase
        .from(table)
        .upsert(data, { onConflict: conflictColumn })
        .select();
    } else {
      request = supabase.from(table).upsert(data).select();
    }

    const { data: resultData, error } = await request;

    // Handle any errors from the upsert operation
    if (error) {
      throw new Error(`Error upserting data: ${error.message}`);
    }

    // If resultData is an array with a single object, return that object
    if (Array.isArray(resultData) && resultData.length === 1) {
      return resultData[0];
    }

    // Return the resultData as is (array or object)
    return resultData;
  } catch (error) {
    // Return an error object if an exception occurs
    console.error("Error in upsertData:", error);
    return { error: error.message };
  }
}

/**
 * Gets user by UID or referral alias from users table
 * @param {string} identifier - User ID or referral alias
 * @returns {Promise<object|null>} User object or null
 */
export async function getUserWithUID(identifier) {
  try {
    if (!identifier) {
      return null;
    }

    // Try to find user by uid or referral_alias in users table
    const { data: user, error } = await supabase
      .from('users')
      .select('*')
      .or(`uid.eq.${identifier},referral_alias.eq.${identifier}`)
      .limit(1)
      .maybeSingle(); // Use maybeSingle() instead of single() to avoid error when no rows found

    if (error) throw error;
    return user;
  } catch (error) {
    console.error("Error getting user:", error);
    return null;
  }
}

/**
 * Increments referral clicks in appropriate tables
 * @param {string} referrerUID - The target user's UID (already converted from alias if needed)
 * @param {string} clickType - Type of referral click ('user' or 'store')
 * @param {string|null} currentUserId - Current logged-in user's UID (null for unauthenticated users)
 */
export async function incrementReferralClicks(referrerUID, clickType, currentUserId) {
  try {
    if (!referrerUID) {
      console.log('No referrer UID provided');
      return false;
    }

    // Self-referral check for authenticated users only
    if (currentUserId) {
      // For authenticated users, we do comprehensive self-referral checking
      if (currentUserId === referrerUID) {
        console.log('Self-referral detected (UID match), skipping increment:', {
          UserId: currentUserId,
          referrerUID: referrerUID,
        });
        return false;
      }
    }

    if (clickType === 'user') {
      // Handle user referral clicks in users_stats_user_referral table
      const { data: userReferralData, error: fetchError } = await supabase
        .from('users_stats_user_referral')
        .select('click_count, uid')
        .eq('uid', referrerUID)
        .maybeSingle();

      if (fetchError) throw fetchError;

      // Update existing record (trigger ensures record exists)
      const newClickCount = (userReferralData?.click_count || 0) + 1;
      const { error: updateError } = await supabase
        .from('users_stats_user_referral')
        .update({ click_count: newClickCount })
        .eq('uid', referrerUID);

      if (updateError) throw updateError;

    } else if (clickType === 'store') {
      // Handle NFT referral clicks in users_master_data table
      const { data: userClickData, error: fetchError } = await supabase
        .from('users_master_data')
        .select('nft_referral_clicks, user_id')
        .eq('user_id', referrerUID)
        .single();

      if (fetchError) throw fetchError;

      const updateData = {
        nft_referral_clicks: (userClickData?.nft_referral_clicks || 0) + 1
      };

      const { error: updateError } = await supabase
        .from('users_master_data')
        .update(updateData)
        .eq('user_id', referrerUID);

      if (updateError) throw updateError;
    }

    return true;
  } catch (error) {
    console.error(`Error incrementing ${clickType} referral clicks:`, error);
    return false;
  }
}

/**
 * Fetches comprehensive referral data for a user from multiple tables
 * @param {string} userId - The user's UID
 * @returns {Promise<object>} Object containing all referral data and any errors
 */
export async function fetchReferralData(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Fetch referral data from Supabase
    const [
      { data: referralStats, error }, 
      { error: purchaseError, count },
      { data: userReferralStats, error: userReferralError }
    ] = await Promise.all([
      supabase
        .from('users_master_data')
        .select(`
          nft_referral_clicks,
          nft_referral_count,
          user_referral_clicks,
          user_referral_count
        `)
        .eq('user_id', userId)
        .single(),
      
      supabase
        .from('products_referral')
        .select('id', { count: 'exact' })
        .eq('referral_from_uid', userId)
        .eq('purchase_status', 'success'),

      supabase
        .from('users_stats_user_referral')
        .select('active_count')
        .eq('uid', userId)
        .single()
    ]);

    // Log errors but don't throw them - return them in the response
    if (error) {
      console.error('Error fetching referral data:', error);
    }

    if (purchaseError) {
      console.error('Error fetching purchase data:', purchaseError);
    }

    if (userReferralError) {
      console.error('Error fetching user referral stats:', userReferralError);
    }

    // Combine all data with defaults
    const combinedData = {
      nft_referral_clicks: referralStats?.nft_referral_clicks ?? 0,
      nft_referral_count: referralStats?.nft_referral_count ?? 0,
      user_referral_clicks: referralStats?.user_referral_clicks ?? 0,
      user_referral_count: referralStats?.user_referral_count ?? 0,
      active_count: userReferralStats?.active_count ?? 0,
      nft_purchases: count ?? 0
    };

    return {
      data: combinedData,
      errors: {
        referralError: error,
        purchaseError,
        userReferralError
      }
    };

  } catch (error) {
    console.error('Error in fetchReferralData:', error);
    return { 
      data: {
        nft_referral_clicks: 0,
        nft_referral_count: 0,
        user_referral_clicks: 0,
        user_referral_count: 0,
        active_count: 0,
        nft_purchases: 0
      },
      error: error.message 
    };
  }
}
